import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Coins, Gift, ArrowRight, Zap, Trophy, History, Share2,
  Sparkles, Star, ArrowLeft, X, Loader2, Award,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatNumber = (n) => Number(n || 0).toLocaleString("fr-FR");

// Rang → couleur d'accent
const RANK_ACCENT = {
  bronze: { from: "from-orange-700", to: "to-amber-900", glow: "shadow-amber-700/30", text: "text-amber-500" },
  silver: { from: "from-slate-400", to: "to-slate-600", glow: "shadow-slate-400/30", text: "text-slate-300" },
  gold: { from: "from-amber-400", to: "to-yellow-600", glow: "shadow-amber-500/30", text: "text-amber-400" },
  platinum: { from: "from-cyan-300", to: "to-blue-500", glow: "shadow-cyan-400/40", text: "text-cyan-300" },
};

const MISSIONS = [
  { id: "share", label: "Partager ARTEKNOS", reward: 50, icon: Share2, color: "emerald", action: "Partager" },
  { id: "rate", label: "Noter une livraison", reward: 25, icon: Star, color: "amber", action: "Noter" },
  { id: "first_order", label: "Première commande", reward: 100, icon: Award, color: "blue", action: "Commander" },
];

export default function LoyaltyPage({ user }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeemMode, setRedeemMode] = useState(null); // null | "free_delivery" | "cash_out"
  const [cashAmount, setCashAmount] = useState(500);
  const [redeeming, setRedeeming] = useState(false);
  const [lastVoucher, setLastVoucher] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const [b, h] = await Promise.all([
        axios.get(`${API}/loyalty/balance/${user.id}`),
        axios.get(`${API}/loyalty/history/${user.id}?limit=20`),
      ]);
      setData(b.data);
      setHistory(h.data || []);
    } catch (err) {
      console.error("Loyalty fetch error:", err);
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Auto-claim welcome bonus si pas encore réclamé
  useEffect(() => {
    if (!user?.id || !data || data.welcome_claimed) return;
    const claim = async () => {
      try {
        const r = await axios.post(`${API}/loyalty/claim-welcome`, { user_id: user.id });
        if (!r.data.already_claimed) {
          toast.success(`+${r.data.amount} ARTEKNOS Coins de bienvenue !`, { duration: 4000 });
          fetchAll();
        }
      } catch (err) {
        console.warn("Welcome claim failed:", err);
      }
    };
    claim();
  }, [user?.id, data, fetchAll]);

  const handleRedeem = async () => {
    if (!user?.id || !redeemMode) return;
    setRedeeming(true);
    try {
      const body = { user_id: user.id, redemption_type: redeemMode };
      if (redeemMode === "cash_out") body.cash_amount = parseInt(cashAmount, 10);
      const r = await axios.post(`${API}/loyalty/redeem`, body);
      if (redeemMode === "free_delivery") {
        setLastVoucher(r.data.voucher_code);
        toast.success("Voucher généré !");
      } else {
        toast.success(r.data.message);
      }
      setRedeemMode(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'échange");
    } finally {
      setRedeeming(false);
    }
  };

  const txLabel = (t) => {
    const map = {
      earn_cashback: "Cashback commande",
      earn_fixed: "Bonus livraison",
      earn_welcome: "Bonus de bienvenue",
      earn_bonus: "Bonus",
      redeem_free_delivery: "Voucher livraison gratuite",
      redeem_cash: "Conversion en cash",
      adjustment: "Ajustement",
    };
    return map[t] || t;
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const today = new Date();
      const isToday = d.toDateString() === today.toDateString();
      if (isToday) return "Aujourd'hui";
      return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
    } catch {
      return iso;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center text-white">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  const balance = data?.balance ?? 0;
  const rankKey = data?.current_rank || "bronze";
  const accent = RANK_ACCENT[rankKey] || RANK_ACCENT.bronze;
  const progressPct = data?.progress_pct ?? 0;
  const pointsToNext = data?.points_to_next ?? 0;
  const nextRankLabel = data?.next_rank_label || "—";
  const balanceFCFA = balance; // 1 coin = 1 FCFA

  return (
    <div className="min-h-screen bg-[#020617] text-white font-sans pb-32" data-testid="loyalty-page">
      {/* Back button */}
      <button
        onClick={() => navigate("/home")}
        className="absolute top-5 left-5 z-30 p-2.5 rounded-xl bg-white/5 backdrop-blur-xl border border-white/10 text-slate-300 hover:text-white hover:border-white/30"
        data-testid="loyalty-back-btn"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>

      {/* ============ HERO HEADER ============ */}
      <div className="relative h-80 flex flex-col items-center justify-center overflow-hidden px-6">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-blue-600/30 rounded-full blur-[100px]" />
        <div className="absolute top-1/3 right-1/4 w-40 h-40 bg-amber-500/15 rounded-full blur-[80px]" />

        <div className="relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 px-4 py-2 rounded-full mb-6">
            <Sparkles size={14} className="text-amber-400" />
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-300">
              Programme Privilège
            </span>
          </div>

          <div className="flex items-center justify-center gap-3 md:gap-4 mb-2">
            <div className="bg-gradient-to-tr from-amber-400 to-yellow-600 p-3 rounded-2xl shadow-[0_0_30px_rgba(251,191,36,0.3)] rotate-12">
              <Coins size={28} className="text-[#020617]" />
            </div>
            <h2 className="text-5xl md:text-6xl font-black italic tracking-tighter" data-testid="coins-balance">
              {formatNumber(balance)}
            </h2>
          </div>
          <p className="text-blue-400 font-black uppercase tracking-[0.4em] text-[10px] md:text-xs">
            ARTEKNOS Coins
          </p>
          <p className="text-slate-500 text-[10px] mt-4 font-medium uppercase tracking-widest">
            Valeur estimée :{" "}
            <span className="text-white font-bold">{formatNumber(balanceFCFA)} FCFA</span>
          </p>
        </div>
      </div>

      <main className="px-5 md:px-8 max-w-3xl mx-auto space-y-7 -mt-6 relative z-20">
        {/* ============ RANK PROGRESS ============ */}
        <div className="bg-white/5 border border-white/10 rounded-[2.5rem] p-5 md:p-6 backdrop-blur-xl" data-testid="rank-progress-card">
          <div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${accent.from} ${accent.to} flex items-center justify-center shadow-lg ${accent.glow}`}>
                <Trophy size={16} className="text-white" />
              </div>
              <span className="text-xs md:text-sm font-black uppercase tracking-tight" data-testid="current-rank-label">
                Rang {data?.current_rank_label}
              </span>
            </div>
            {data?.next_rank ? (
              <span className="text-[10px] text-slate-500 font-bold">
                Plus que <span className="text-white font-black">{formatNumber(pointsToNext)} pts</span> pour{" "}
                <span className="text-blue-400 uppercase">{nextRankLabel}</span>
              </span>
            ) : (
              <span className="text-[10px] text-cyan-300 font-black uppercase tracking-widest">Rang max atteint</span>
            )}
          </div>
          <div className="h-3 bg-white/5 rounded-full overflow-hidden p-0.5 border border-white/5">
            <div
              className="h-full bg-gradient-to-r from-blue-600 to-cyan-400 rounded-full shadow-[0_0_15px_rgba(37,99,235,0.5)] transition-all duration-700"
              style={{ width: `${progressPct}%` }}
              data-testid="rank-progress-bar"
            />
          </div>
          <p className="text-[9px] text-slate-600 mt-2 font-bold uppercase tracking-widest">
            Lifetime gagnés : {formatNumber(data?.lifetime_earned || 0)} coins
          </p>
        </div>

        {/* ============ REWARDS GRID ============ */}
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          <button
            onClick={() => balance >= 1500 ? setRedeemMode("free_delivery") : toast.info("Continuez vos courses pour débloquer cette récompense")}
            disabled={balance < 1500}
            className={`bg-gradient-to-br from-blue-600 to-indigo-700 rounded-[1.5rem] md:rounded-[2rem] p-5 flex flex-col justify-between h-44 md:h-48 text-left shadow-xl shadow-blue-900/20 hover:scale-[1.02] active:scale-[0.98] transition-all ${balance < 1500 ? "opacity-50" : ""}`}
            data-testid="reward-free-delivery"
          >
            <Zap size={26} className="text-white" />
            <div>
              <h3 className="font-black text-base md:text-lg leading-tight uppercase italic text-white">
                Course<br />Gratuite
              </h3>
              <p className="text-blue-100/70 text-[10px] mt-1 uppercase font-bold tracking-widest">
                1 500 Coins
              </p>
            </div>
          </button>

          <button
            onClick={() => balance >= 500 ? setRedeemMode("cash_out") : toast.info("Minimum 500 coins requis")}
            disabled={balance < 500}
            className={`bg-white/5 border border-white/10 rounded-[1.5rem] md:rounded-[2rem] p-5 flex flex-col justify-between h-44 md:h-48 text-left hover:bg-white/10 active:scale-[0.98] transition-all ${balance < 500 ? "opacity-50" : ""}`}
            data-testid="reward-cash-out"
          >
            <Gift size={26} className="text-pink-500" />
            <div>
              <h3 className="font-black text-base md:text-lg leading-tight uppercase italic text-slate-200">
                Convertir<br />en Cash
              </h3>
              <p className="text-slate-500 text-[10px] mt-1 uppercase font-bold tracking-widest">
                Dès 500 Coins
              </p>
            </div>
          </button>
        </div>

        {/* ============ VOUCHER ACTIF ============ */}
        {lastVoucher && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-5 flex items-center justify-between gap-3" data-testid="voucher-active">
            <div>
              <p className="text-[10px] font-black text-emerald-300 uppercase tracking-widest mb-1">Voucher actif</p>
              <p className="text-lg font-black tracking-tight text-white font-mono">{lastVoucher}</p>
            </div>
            <button
              onClick={() => {
                navigator.clipboard?.writeText(lastVoucher);
                toast.success("Code copié");
              }}
              className="px-4 py-2 rounded-xl bg-emerald-500/20 text-emerald-200 text-[10px] font-black uppercase tracking-widest hover:bg-emerald-500/30"
            >
              Copier
            </button>
          </div>
        )}

        {/* ============ MISSIONS DU JOUR (statique) ============ */}
        <div className="space-y-3">
          <h4 className="text-xs font-black uppercase tracking-[0.2em] text-slate-500 ml-2">Missions du jour</h4>
          {MISSIONS.map((m) => {
            const Icon = m.icon;
            return (
              <div
                key={m.id}
                className="bg-white/5 border border-white/10 rounded-3xl p-4 md:p-5 flex items-center justify-between group active:scale-[0.99] transition-all"
                data-testid={`mission-${m.id}`}
              >
                <div className="flex items-center gap-3 md:gap-4">
                  <div className={`w-10 h-10 bg-${m.color}-500/10 rounded-xl flex items-center justify-center text-${m.color}-500`}>
                    <Icon size={18} />
                  </div>
                  <div>
                    <p className="text-xs font-black uppercase italic tracking-tight text-white">{m.label}</p>
                    <p className="text-[10px] text-slate-500 font-bold">+{m.reward} Coins</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (m.id === "share" && navigator.share) {
                      navigator.share({ title: "WÔMA de ARTEKNOS", url: window.location.origin });
                    } else if (m.id === "rate") {
                      navigate("/dashboard");
                    } else if (m.id === "first_order") {
                      navigate("/home");
                    }
                  }}
                  className="bg-white/5 px-3 md:px-4 py-2 rounded-xl text-[10px] font-black uppercase border border-white/10 group-hover:bg-blue-600 group-hover:border-blue-600 transition-all"
                >
                  {m.action}
                </button>
              </div>
            );
          })}
        </div>

        {/* ============ HISTORIQUE ============ */}
        <div className="bg-white/[0.02] border border-white/5 rounded-[2rem] md:rounded-[2.5rem] p-5 md:p-6">
          <div className="flex justify-between items-center mb-5">
            <div className="flex items-center gap-2">
              <History size={14} className="text-slate-500" />
              <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500">Historique des points</h4>
            </div>
          </div>

          {history.length === 0 ? (
            <p className="text-center text-slate-600 italic py-6 text-xs">Aucune transaction</p>
          ) : (
            <div className="space-y-4" data-testid="loyalty-history">
              {history.map((tx) => (
                <div key={tx.id} className="flex justify-between items-center">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-slate-200 truncate">{tx.description || txLabel(tx.type)}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-widest">{formatDate(tx.created_at)}</p>
                  </div>
                  <span className={`text-xs md:text-sm font-black flex-shrink-0 ml-3 ${tx.delta > 0 ? "text-blue-400" : "text-red-400"}`}>
                    {tx.delta > 0 ? "+" : ""}{formatNumber(tx.delta)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* ============ FOOTER CTA ============ */}
      <div className="fixed bottom-0 left-0 right-0 p-5 md:p-6 bg-gradient-to-t from-[#020617] via-[#020617]/90 to-transparent z-20">
        <button
          onClick={() => {
            if (balance >= 1500) setRedeemMode("free_delivery");
            else if (balance >= 500) setRedeemMode("cash_out");
            else toast.info("Solde insuffisant pour échanger");
          }}
          className="w-full max-w-3xl mx-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white py-4 md:py-5 rounded-[1.5rem] md:rounded-[2rem] font-black text-xs md:text-sm flex items-center justify-center gap-3 shadow-[0_15px_40px_rgba(37,99,235,0.3)] transition-all uppercase tracking-widest"
          data-testid="spend-coins-cta"
        >
          DÉPENSER MES COINS <ArrowRight size={18} />
        </button>
      </div>

      {/* ============ REDEEM MODAL ============ */}
      {redeemMode && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[200] flex items-end md:items-center justify-center p-4" data-testid="redeem-modal">
          <div className="bg-[#0F172A] border border-white/10 rounded-[2.5rem] p-6 md:p-8 w-full max-w-md relative animate-in slide-in-from-bottom duration-300">
            <button
              onClick={() => setRedeemMode(null)}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/5 text-slate-400 hover:text-white"
            >
              <X size={18} />
            </button>
            {redeemMode === "free_delivery" ? (
              <>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center mb-4 shadow-lg shadow-blue-900/30">
                  <Zap size={26} className="text-white" />
                </div>
                <h3 className="text-xl font-black italic uppercase tracking-tight mb-2">Course Gratuite</h3>
                <p className="text-sm text-slate-400 mb-6">
                  Échangez <span className="text-amber-400 font-black">1 500 Coins</span> contre une livraison standard gratuite (jusqu'à 5 000 FCFA).
                </p>
              </>
            ) : (
              <>
                <div className="w-14 h-14 rounded-2xl bg-pink-500/20 flex items-center justify-center mb-4">
                  <Gift size={26} className="text-pink-400" />
                </div>
                <h3 className="text-xl font-black italic uppercase tracking-tight mb-2">Convertir en Cash</h3>
                <p className="text-sm text-slate-400 mb-4">
                  Convertissez vos coins en crédit ARTEKNOS (1 coin = 1 FCFA).
                </p>
                <div className="space-y-3 mb-6">
                  <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Montant à convertir</p>
                  <div className="flex gap-2 flex-wrap">
                    {[500, 1000, 2000, 5000].filter((v) => v <= balance).map((v) => (
                      <button
                        key={v}
                        onClick={() => setCashAmount(v)}
                        className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest border transition-all ${
                          cashAmount === v
                            ? "bg-pink-500 text-white border-transparent"
                            : "bg-white/5 text-slate-300 border-white/10 hover:border-pink-500/50"
                        }`}
                        data-testid={`cash-amount-${v}`}
                      >
                        {formatNumber(v)} F
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
            <button
              onClick={handleRedeem}
              disabled={redeeming}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-blue-600/20 hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="confirm-redeem-btn"
            >
              {redeeming && <Loader2 size={14} className="animate-spin" />}
              Confirmer l'échange
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
